import React from "react";
import { Lock, X } from "lucide-react";
import { ModalLayer } from "./Sheet";
import * as api from "../utils/api";
import { useAuthForm } from "../hooks/useAuthForm";

interface Props {
  auth: ReturnType<typeof useAuthForm>;
}

// Карточка «Параметры проверки» для неавторизованного пользователя: переключатель
// вход/регистрация, форма, согласия (#387), текст соглашения по клику. Вся логика —
// в хуке useAuthForm (App.tsx создаёт его и передаёт результат целиком, props здесь
// не плодим по одному на каждое поле).
export default function AuthPanel({ auth }: Props) {
  const {
    authMode, setAuthMode, authError, setAuthError, authBanner, setAuthBanner,
    checkEmail, setCheckEmail, authForm, setAuthForm, consents, setConsents,
    legalDocs, legalOpen, setLegalOpen, openLegal, consentsOk,
    handleAuthSubmit, handleResendConfirmation,
  } = auth;

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-100/50 space-y-5">
      <div className="space-y-1.5">
        <h3 className="font-extrabold text-slate-900 tracking-tight font-display flex items-center gap-2">
          <Lock className="w-5 h-5 text-indigo-600 shrink-0" />
          Параметры проверки
        </h3>
        <p className="text-xs text-slate-500">
          Ввод параметров проверки и запуск экспресс-анализа доступен только зарегистрированным пользователям.
        </p>
      </div>

      {/* Tab selector for Login / Register */}
      <div className="grid grid-cols-2 p-1 bg-slate-50 rounded-xl border border-slate-150">
        <button
          type="button"
          onClick={() => { setAuthMode("login"); setAuthError(null); setAuthBanner(null); setCheckEmail(null); }}
          className={`py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
            authMode === "login"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          Войти
        </button>
        <button
          type="button"
          onClick={() => { setAuthMode("register"); setAuthError(null); setAuthBanner(null); setCheckEmail(null); }}
          className={`py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
            authMode === "register"
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-slate-900"
          }`}
        >
          Регистрация
        </button>
      </div>

      {authBanner && (
        <div className={`p-3 border text-xs rounded-lg font-medium ${
          authBanner.kind === "ok"
            ? "bg-emerald-50 border-emerald-100 text-emerald-800"
            : "bg-amber-50 border-amber-100 text-amber-800"
        }`}>
          {authBanner.text}
        </div>
      )}

      {authError && (
        <div className="p-3 bg-rose-50 border border-rose-100 text-rose-800 text-xs rounded-lg font-medium">
          {authError}
          {/^.*[Ee]mail не подтверждён.*$/.test(authError) && (
            <button
              type="button"
              onClick={handleResendConfirmation}
              className="block w-full mt-2 py-1.5 bg-white hover:bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-bold rounded-md transition-colors cursor-pointer"
            >
              Выслать письмо подтверждения ещё раз
            </button>
          )}
        </div>
      )}

      {checkEmail ? (
        <div className="space-y-4 text-center">
          <div className="text-4xl">📧</div>
          <p className="text-sm text-slate-700">
            Письмо со ссылкой подтверждения отправлено на<br/>
            <strong className="font-mono text-slate-900">{checkEmail}</strong>
          </p>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Откройте письмо и перейдите по ссылке, чтобы активировать аккаунт.
            После подтверждения вернитесь сюда и войдите под своим логином.
          </p>
          <button
            type="button"
            onClick={() => { setCheckEmail(null); setAuthMode("login"); }}
            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs uppercase tracking-wide rounded-xl transition-all cursor-pointer"
          >
            К окну входа
          </button>
        </div>
      ) : (
        <form onSubmit={handleAuthSubmit} className="space-y-3.5">
          {/* Регистрация физлицом (#385): ФИО, email, телефон, пароль.
              Компания (ЮЛ или ИП) заводится потом — в кабинете, разделе
              «Организация»: там тип определяется по ИНН и поля свои. */}
          {authMode === "register" && (
            <div className="space-y-1">
              <label htmlFor="auth-fullname" className="text-[11px] font-semibold text-slate-600">ФИО <span className="text-indigo-500">*</span></label>
              <input id="auth-fullname" autoComplete="name" autoCapitalize="words" enterKeyHint="next"
                type="text" placeholder="Иванов Иван Иванович"
                value={authForm.fullName}
                onChange={(e) => setAuthForm({ ...authForm, fullName: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl outline-none transition-all text-base sm:text-xs text-slate-850"
                required minLength={3} maxLength={255}
              />
            </div>
          )}

          {authMode === "login" && (
            <div className="space-y-1">
              <label htmlFor="auth-login" className="text-[11px] font-semibold text-slate-600">Email или логин</label>
              <input id="auth-login" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" enterKeyHint="next"
                type="text"
                placeholder="mail@example.com"
                value={authForm.username}
                onChange={(e) => setAuthForm({ ...authForm, username: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl outline-none transition-all text-base sm:text-xs text-slate-850 font-mono"
                required maxLength={255}
              />
            </div>
          )}

          {authMode === "register" && (
            <div className="space-y-1">
              <label htmlFor="auth-email" className="text-[11px] font-semibold text-slate-600">
                Email <span className="text-slate-400 font-normal">(на него придёт письмо подтверждения, по нему же вход)</span>
              </label>
              <input id="auth-email" autoComplete="email" autoCapitalize="none" enterKeyHint="next"
                type="email"
                placeholder="mail@example.com"
                value={authForm.email}
                onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl outline-none transition-all text-base sm:text-xs text-slate-850"
                required
              />
            </div>
          )}

          {authMode === "register" && (
            <div className="space-y-1">
              <label htmlFor="auth-phone" className="text-[11px] font-semibold text-slate-600">Телефон <span className="text-indigo-500">*</span></label>
              <input id="auth-phone" autoComplete="tel" inputMode="tel" enterKeyHint="next"
                type="tel" placeholder="+7 (999) 999-99-99"
                value={authForm.phone}
                onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl outline-none transition-all text-base sm:text-xs text-slate-850"
                required maxLength={32}
              />
            </div>
          )}

          <div className="space-y-1">
            <label htmlFor="auth-password" className="text-[11px] font-semibold text-slate-600">
              Пароль {authMode === "register" && <span className="text-slate-400 font-normal">(минимум 8 символов)</span>}
            </label>
            <input id="auth-password" autoComplete={authMode === "register" ? "new-password" : "current-password"}
              enterKeyHint={authMode === "register" ? "next" : "go"}
              type="password"
              placeholder="••••••••"
              value={authForm.password}
              onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl outline-none transition-all text-base sm:text-xs text-slate-850"
              required
              minLength={authMode === "register" ? 8 : undefined}
            />
          </div>

          {authMode === "register" && (
            <div className="space-y-1">
              <label htmlFor="auth-password2" className="text-[11px] font-semibold text-slate-600">Повторите пароль <span className="text-indigo-500">*</span></label>
              <input id="auth-password2" autoComplete="new-password" enterKeyHint="go"
                type="password" placeholder="••••••••"
                value={authForm.repeatPassword}
                onChange={(e) => setAuthForm({ ...authForm, repeatPassword: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white rounded-xl outline-none transition-all text-base sm:text-xs text-slate-850"
                required minLength={8}
              />
            </div>
          )}

          {authMode === "register" && (
            <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2">
              Компанию — ООО или ИП — зарегистрируете позже в личном кабинете, в разделе «Организация».
              Анализ и подбор доступны сразу после подтверждения email.
            </p>
          )}

          {/* Согласия (#387). Обязательные — со звёздочкой; текст каждого
              открывается по ссылке, редакцию правит администратор в справке. */}
          {authMode === "register" && (
            <div className="space-y-2 pt-1">
              {([
                ["license", "Я соглашаюсь с правилами лицензионного соглашения системы СНС Монитор", true],
                ["pdn", "Я даю согласие на обработку персональных данных (ст. 9 ФЗ-152)", true],
                ["third_party", "Я даю согласие на передачу данных третьим лицам (ст. 7 ФЗ-152)", true],
                ["ads", "Я даю согласие на получение рекламы (ст. 18 ФЗ «О рекламе»)", false],
              ] as [api.ConsentKind, string, boolean][]).map(([kind, label, required]) => {
                const doc = legalDocs.find((d) => d.kind === kind);
                return (
                  <label key={kind} className="flex items-start gap-2 text-[11px] text-slate-600 cursor-pointer">
                    <input
                      type="checkbox" checked={consents[kind]}
                      onChange={(e) => setConsents({ ...consents, [kind]: e.target.checked })}
                      className="mt-0.5 w-3.5 h-3.5 accent-indigo-600 cursor-pointer shrink-0"
                    />
                    <span>
                      {label}{required && <span className="text-indigo-500"> *</span>}
                      {doc && (
                        <>
                          {" "}
                          <button type="button" onClick={() => openLegal(doc.slug, doc.title)}
                            className="text-indigo-500 hover:text-indigo-600 underline underline-offset-2 cursor-pointer">
                            текст
                          </button>
                        </>
                      )}
                    </span>
                  </label>
                );
              })}
              {!consentsOk && (
                <p className="text-[11px] text-slate-400">
                  Обязательные согласия отмечены звёздочкой. Согласие на рекламу — по желанию, на доступ к сервису оно не влияет.
                </p>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={authMode === "register" && !consentsOk}
            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs uppercase tracking-wide rounded-xl transition-all cursor-pointer mt-2"
          >
            {authMode === "login" ? "Войти в систему" : "Зарегистрироваться"}
          </button>
        </form>
      )}

      {/* Текст соглашения: читается до регистрации, редакция — из справки. */}
      {legalOpen && (
        <div className="fixed inset-0 z-(--z-sheet) bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setLegalOpen(null)}>
          <ModalLayer onClose={() => setLegalOpen(null)} />
          <div role="dialog" aria-modal="true" className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92dvh] sm:max-h-[80vh] overflow-auto overscroll-contain pb-[env(safe-area-inset-bottom)]"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100 sticky top-0 bg-white">
              <h3 className="font-bold text-slate-900 text-sm">{legalOpen.title}</h3>
              <button onClick={() => setLegalOpen(null)} aria-label="Закрыть" className="grid place-items-center w-11 h-11 -m-3 shrink-0 text-slate-400 hover:text-slate-700 cursor-pointer">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 text-xs text-slate-700 whitespace-pre-line leading-relaxed">{legalOpen.body}</div>
          </div>
        </div>
      )}
    </div>
  );
}
